/** @format */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LabelInput from "../../../shared/components/labelInput";
import Tq3Mapper from "../mappers/tq3Mapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import { handleNext } from "../../../shared/utils/navigationUtils";
import useQuoteStore from "../store/QuoteStore";

import {
  fetchEmp,
  fetchEmpBusiness,
  fetchMartialStatus,
  fetchOccupation,
  fetchVehCover,
} from "../../../shared/services/simpleListItemsService";
import NewDateTimePicker from "../../../shared/components/newDateTimePicker";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import * as Sentry from "@sentry/react";
import RadioGroup from "../../../shared/components/RadioGroup";

const Tq3 = () => {
  // Store
  // Form Setup
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    setValue,
    trigger,
    handleSubmit,
  } = useForm({
    defaultValues: Tq3Mapper.get(),
  });

  const STATE = useAppStateStore();

  // State
  const [occupation, setOccupation] = useState([]);
  const [employeeBusiness, setEmployeeBusiness] = useState([]);
  const [employee, setEmployee] = useState([]);
  const [coverLevel, setCoverLevel] = useState([]);
  const [martial, setMartial] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const prioritizedOccupations = ["Taxi Driver", "Taxi Operating", "Chauffeur"];
  useEffect(() => {
    if (watch("DriverResidecy") === "Yes") {
      const driverDOB = useQuoteStore.getState().quote?.risk?.drivers[0]
        ?.dateOfBirth;

      if (driverDOB) {
        const birthDate = new Date(driverDOB); // "1964-02-19T00:00:00" format will work directly with new Date()
        const today = new Date();

        let residentYears = today.getFullYear() - birthDate.getFullYear();

        // Adjust age if birthday hasn't occurred this year
        if (
          today.getMonth() < birthDate.getMonth() ||
          (today.getMonth() === birthDate.getMonth() &&
            today.getDate() < birthDate.getDate())
        ) {
          residentYears--;
        }

        setValue("DriverYears", residentYears);
      }
    }
  }, [watch("DriverResidecy")]);

  const prioritizeOccupations = (array) => {
    const priorityItems = [];
    const otherItems = [];

    array.forEach((item) => {
      if (prioritizedOccupations.includes(item.listItemDescription)) {
        priorityItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    priorityItems.sort((a, b) => {
      return (
        prioritizedOccupations.indexOf(a.listItemDescription) -
        prioritizedOccupations.indexOf(b.listItemDescription)
      );
    });

    return [...priorityItems, ...otherItems];
  };

  const convertArrayFormat = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }

    const prioritizedArray = prioritizeOccupations(array);
    return prioritizedArray.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [
          martialData,
          occupationData,
          empBusinessData,
          empData,
          coverLevelData,
        ] = await Promise.all([
          fetchMartialStatus(),
          fetchOccupation(),
          fetchEmpBusiness(),
          fetchEmp(),
          fetchVehCover(),
        ]);

        setMartial(martialData);
        setOccupation(occupationData);
        setEmployeeBusiness(empBusinessData);
        setEmployee(empData);
        setCoverLevel(coverLevelData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (watch("driverOccupation")?.value === "D34") {
      setValue("BusinessOccupation", { value: "703", label: "" });
    }
  }, [watch("driverOccupation")]);

  // done to retrigger the date validation, react-hook-form's known fault.
  let datePassedTest = watch("date");
  useEffect(() => {
    if (datePassedTest) trigger("date");
  }, [datePassedTest]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ3_ENTERED);
  }, []);

  const onSubmit = (data) => {
    Tq3Mapper.set(data);
    if (watch("date") !== null && watch("date") !== "Invalid Date") {
      if (isValid) {
        TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ3_NEXT_CLICKED);
      }
      handleNext(QuoteStep.TQ4);
      STATE.setHideTq2Questions(false);
    }
  };

  const handleVehCoverMenuOpen = () => {
    if (!isLoading) {
      if (Array.isArray(coverLevel) && coverLevel.length === 0) {
        Sentry.captureMessage("vehCover dropdown opened with empty options", {
          level: "warning",
          tags: "/vehCover",
        });
      }
    }
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <h1 className="margin-bottom-10">Cover Details</h1>
          <p className="margin-bottom-30">
            Please provide your vehicle information.
          </p>

          <div className="fields">
            <NewDateTimePicker
              name="date"
              label="When would you like your cover to start?"
              errors={errors.date}
              setValue={setValue}
              control={control}
              rules={{
                required: "Date is required",
              }}
              watch={watch}
              defaultValue={watch("date")}
              minDate={new Date()}
              maxDate={new Date(new Date().setDate(new Date().getDate() + 29))}
            />

            <LabelInput
              id="coverLevel"
              label="Cover Level"
              type="select"
              options={convertArrayFormat(coverLevel)}
              control={control}
              placeholder="Select cover level"
              register={register("coverLevel", {
                required: true,
              })}
              watch={watch("coverLevel")}
              trigger={trigger}
              onMenuOpen={handleVehCoverMenuOpen}
              isLoading={isLoading}
              errors={errors.coverLevel}
            />
            {watch("coverLevel") && (
              <p className="gray-text font-12 margin-top-minus5 margin-bottom-15">
                {(() => {
                  const coverLevel = watch("coverLevel");

                  if (coverLevel?.value === "01") {
                    return "Provides cover for damage to your own car, in addition to third-party damage.";
                  } else if (coverLevel?.value === "02") {
                    return "Provides cover for third-party claims, plus fire & theft damage to your car.";
                  } else if (coverLevel?.value === "03") {
                    return "Provides cover for third-party claims only.";
                  } else {
                    return "";
                  }
                })()}
              </p>
            )}

            <LabelInput
              id="driverMaritalStatus"
              label="Marital status"
              type="select"
              options={convertArrayFormat(martial)}
              control={control}
              placeholder="Select martial status"
              register={register("driverMaritalStatus", { required: true })}
              watch={watch("driverMaritalStatus")}
              trigger={trigger}
              isLoading={isLoading}
              errors={errors.driverMaritalStatus}
            />

            {STATE.hideTq2Questions == false && (
              <>
                <LabelInput
                  id="driverOccupation"
                  label="Occupation"
                  type="select"
                  options={convertArrayFormat(occupation)}
                  control={control}
                  placeholder="Select occupation"
                  register={register("driverOccupation", { required: true })}
                  errors={errors.driverOccupation}
                  watch={watch("driverOccupation")}
                  trigger={trigger}
                  isSearchable={true}
                  isLoading={isLoading}
                />

                <LabelInput
                  id="BusinessOccupation"
                  label="Business type"
                  type="select"
                  options={convertArrayFormat(employeeBusiness)}
                  control={control}
                  placeholder="Select business occupation"
                  register={register("BusinessOccupation", { required: true })}
                  errors={errors.BusinessOccupation}
                  watch={watch("BusinessOccupation")}
                  trigger={trigger}
                  isSearchable={true}
                  isLoading={isLoading}
                />
                <LabelInput
                  id="empStatus"
                  label="Employee status "
                  type="select"
                  options={convertArrayFormat(employee)}
                  control={control}
                  placeholder="Select status"
                  register={register("empStatus", { required: true })}
                  errors={errors.empStatus}
                  watch={watch("empStatus")}
                  trigger={trigger}
                  isLoading={isLoading}
                />
              </>
            )}

            <RadioGroup
              name="DriverPartTime"
              mainLabel="Do you have part-time occupation?"
              register={register}
              errors={errors.DriverPartTime}
            />

            {watch("DriverPartTime") === "Yes" && (
              <>
                <LabelInput
                  id="partTimeOccup"
                  label="Part time occupation"
                  type="select"
                  options={convertArrayFormat(occupation)}
                  control={control}
                  placeholder="Select part time occupation"
                  register={register("partTimeOccup", {
                    required: true,
                  })}
                  watch={watch("partTimeOccup")}
                  trigger={trigger}
                  isSearchable={true}
                />
                {errors.partTimeOccup && (
                  <span className="errorField radioError">Required</span>
                )}

                <LabelInput
                  id="partTimeBusinessOccupation"
                  label="Part time business type"
                  type="select"
                  options={convertArrayFormat(employeeBusiness)}
                  control={control}
                  placeholder="Select part time business occupation"
                  register={register("partTimeBusinessOccupation", {
                    required: true,
                  })}
                  watch={watch("partTimeBusinessOccupation")}
                  trigger={trigger}
                  isSearchable={true}
                />
                {errors.partTimeBusinessOccupation && (
                  <span className="errorField radioError">Required</span>
                )}

                <LabelInput
                  id="parttimeempStatus"
                  label="Part time employee status "
                  type="select"
                  options={convertArrayFormat(employee)}
                  control={control}
                  placeholder="Select part time employee status"
                  register={register("parttimeempStatus", { required: true })}
                  watch={watch("parttimeempStatus")}
                  trigger={trigger}
                />
                {errors.parttimeempStatus && (
                  <span className="errorField radioError">Required</span>
                )}
              </>
            )}

            <RadioGroup
              name="DriverResidecy"
              mainLabel="Have you been resident in the UK since birth?"
              register={register}
              errors={errors.DriverResidecy}
              onNo={() => {
                setValue("DriverYears", null);
              }}
            />

            {watch("DriverResidecy") === "No" && (
              <LabelInput
                id="DriverYears"
                label="How many years have you been resident in the UK?"
                type="number"
                placeholder="Enter years"
                register={register("DriverYears", {
                  required: "Required",
                  pattern: {
                    value: /^\d{1,2}$/,
                    message: "The maximum number of digits is 2",
                  },
                })}
                errors={errors.DriverYears}
                watch={watch("DriverYears")}
                pattern="[0-9]*"
                trigger={trigger}
              />
            )}
          </div>
        </QuoteFormWrapper>
        <FormNavigationButtons
          prevStep={QuoteStep.TQ2}
          isValid={
            isValid &&
            watch("date") !== null &&
            watch("date") !== "Invalid Date"
          }
        />
      </form>
    </>
  );
};

export default Tq3;
